import React, { useCallback, useMemo } from "react";
import { connect } from "react-redux";

import { EntityType } from "@mapmycustomers/shared/enum";
import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import FilterModel from "@mapmycustomers/shared/types/viewModel/internalModel/FilterModel";
import { ModifiedMapFilterModel } from "@mapmycustomers/shared/types/viewModel/internalModel/MapFilterModel";
import { MapSavedFilter } from "@mapmycustomers/shared/types/viewModel/SavedFilter";

import {
  FrequencyStatusFilter,
  NearbyFilter,
  NextActivityFilter,
  NoRecentContactFilter,
  RecentlyAddedFilter,
} from "@app/component/filtering";
import MapFilterButton from "@app/component/filtering/components/MapFilterButton";
import Interactive from "@app/component/map/FeaturedMap/essentials/controls/Interactive";
import useMapView from "@app/component/view/utils/useMapView";
import EntityVisibility from "@app/scene/map/components/EntityVisibility";
import { SavedFilterButton } from "@app/scene/map/components/filtering";
import { isCurrentUserMember } from "@app/store/iam";
import { setOfferedFilters } from "@app/store/map/actions";
import { RootState } from "@app/store/rootReducer";
import { getFavoriteSavedFilters } from "@app/store/savedFilters";
import universalFieldsFieldModel from "@app/util/fieldModel/universalFieldsFieldModel";

import styles from "./FilterBar.module.scss";

interface Props {
  favoriteSavedFilters: MapSavedFilter[];
  isMember?: boolean;
  onSetCustomFilters: (customFilters: ModifiedMapFilterModel) => void;
}

const FilterBar: React.FC<Props> = ({ favoriteSavedFilters, isMember, onSetCustomFilters }) => {
  const {
    updateFilterModel,
    viewState: { filter },
  } = useMapView();

  const universalFilter = filter.universal!;

  const implicitFields = useMemo(() => [universalFieldsFieldModel.getByName("territories")!], []);
  const hiddenFields = useMemo(() => {
    const viewAs = universalFieldsFieldModel.fields.find((field) =>
      field.hasFeature(FieldFeature.VIEW_AS_FIELD)
    );
    if (!viewAs) {
      return implicitFields;
    }
    // viewAs filter is not available for members
    return isMember ? [...implicitFields, viewAs] : implicitFields;
  }, [implicitFields, isMember]);

  const universalSetCustomFilters = useCallback(
    (filter: Partial<FilterModel>) => onSetCustomFilters({ universal: filter }),
    [onSetCustomFilters]
  );

  const universalUpdateFilterModel = useCallback(
    (filter: Partial<FilterModel>) => updateFilterModel({ universal: filter }),
    [updateFilterModel]
  );

  return (
    <div className={styles.container}>
      <MapFilterButton
        context="mapPage"
        hiddenFields={hiddenFields}
        implicitFilterFields={implicitFields}
      />
      <EntityVisibility />
      {favoriteSavedFilters.map((savedFilter) => (
        <Interactive key={savedFilter.id}>
          <SavedFilterButton implicitFilterFields={implicitFields} savedFilter={savedFilter} />
        </Interactive>
      ))}
      <Interactive>
        <NearbyFilter
          filter={universalFilter}
          onSetCustomFilters={universalSetCustomFilters}
          onUpdateFilterModel={universalUpdateFilterModel}
        />
      </Interactive>
      <Interactive>
        <NoRecentContactFilter
          filter={universalFilter}
          onSetCustomFilters={universalSetCustomFilters}
          onUpdateFilterModel={universalUpdateFilterModel}
        />
      </Interactive>
      <Interactive>
        <FrequencyStatusFilter
          filter={universalFilter}
          onSetCustomFilters={universalSetCustomFilters}
          onUpdateFilterModel={universalUpdateFilterModel}
        />
      </Interactive>
      <Interactive>
        <RecentlyAddedFilter
          filter={universalFilter}
          onSetCustomFilters={universalSetCustomFilters}
          onUpdateFilterModel={universalUpdateFilterModel}
        />
      </Interactive>
      <Interactive>
        <NextActivityFilter
          filter={universalFilter}
          onSetCustomFilters={universalSetCustomFilters}
          onUpdateFilterModel={universalUpdateFilterModel}
        />
      </Interactive>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  favoriteSavedFilters: getFavoriteSavedFilters(state)(EntityType.PIN) as MapSavedFilter[],
  isMember: isCurrentUserMember(state),
});

const mapDispatchToProps = {
  onSetCustomFilters: setOfferedFilters,
};

export default connect(mapStateToProps, mapDispatchToProps)(FilterBar);
